// The path of the die over the board: where it is and how it is turned at each
// moment of the roll. Pure. The face is known before the die moves, so the
// plan starts from the landing and works back to the start. That makes the die
// land on its spot, with the rolled face up, with no correction at the end.
//
// The path is a straight line in a plane where the area repeats as a mirror
// image at each wall. Folded back into the area, the line is a path that
// bounces. The die turns as a ball that rolls along the path, and each wall
// gives it a new axis.

import {
  TUMBLE_BONK_PORTION,
  TUMBLE_BONK_SQUASH,
  TUMBLE_BOUNCE_COUNTS,
  TUMBLE_EASE_POWER,
  TUMBLE_GROW_PORTION,
  TUMBLE_ROLL_RATE,
  TUMBLE_START_SCALE,
  TUMBLE_TWIST,
} from '../constants';
import type { Rng } from '../lib/rng';
import { landingOrientation } from './octahedron';
import { axisAngle, type Mat3, multiply, normalize, type Vec3 } from './vector';

export type Point = { x: number; y: number };

/** Where the center of the die may go. */
export type TumbleArea = { left: number; top: number; right: number; bottom: number };

export type TumblePose = {
  x: number;
  y: number;
  scale: number;
  orientation: Mat3;
};

/** The stretch of path between two walls. Distances count back from the landing. */
type Leg = {
  /** The distance from the landing at which this leg ends in time. */
  from: number;
  axis: Vec3;
  /** How the die is turned at `from`. */
  rest: Mat3;
};

export type TumblePlan = {
  area: TumbleArea;
  /** The landing, as its mirror image in the plane of the straight line. */
  landingImage: Point;
  /** One unit along the line, from the landing image toward the start. */
  towardStart: Point;
  length: number;
  radius: number;
  /** In order of distance from the landing. */
  legs: readonly Leg[];
  /** The progress of the roll at each wall, in order of time. */
  bounces: readonly number[];
};

export type TumbleRequest = {
  area: TumbleArea;
  start: Point;
  landing: Point;
  radius: number;
  face: number;
  rng: Rng;
};

/** A wall this near to an end of the path does not count. */
const WALL_TOLERANCE = 1e-6;

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high);

/** The last item that passes, in a list sorted so that the items that pass come first. */
const lastWhere = <T>(items: readonly T[], passes: (item: T) => boolean): T | undefined =>
  items.filter(passes).at(-1);

/** Folds a coordinate of the mirror plane back into `0..span`. */
const fold = (value: number, span: number): number => {
  if (span <= 0) return 0;
  const period = span * 2;
  const wrapped = ((value % period) + period) % period;
  return wrapped > span ? period - wrapped : wrapped;
};

/** A copy of the area with an odd index is a mirror image. */
const isMirrored = (cell: number): boolean => Math.abs(cell) % 2 === 1;

const imageOf = (coordinate: number, span: number, cell: number): number =>
  cell * span + (isMirrored(cell) ? span - coordinate : coordinate);

/** The distances along the line at which one coordinate meets a wall. */
const wallsAlong = (from: number, step: number, length: number, span: number): number[] => {
  if (span <= 0 || step === 0) return [];
  const to = from + step * length;
  const first = Math.ceil(Math.min(from, to) / span);
  const last = Math.floor(Math.max(from, to) / span);
  const walls: number[] = [];
  for (let cell = first; cell <= last; cell += 1) {
    const distance = (cell * span - from) / step;
    if (distance > WALL_TOLERANCE && distance < length - WALL_TOLERANCE) walls.push(distance);
  }
  return walls;
};

/** A count of walls for each direction, each with a random sign. Both are one or more. */
const pickCells = (rng: Rng): Point => {
  const bounces = rng.pick(TUMBLE_BOUNCE_COUNTS);
  const across = 1 + rng.int(bounces - 1);
  const sign = (): number => (rng.next() < 0.5 ? -1 : 1);
  return { x: across * sign(), y: (bounces - across) * sign() };
};

const progressAt = (distance: number, length: number): number =>
  length === 0 ? 1 : 1 - (distance / length) ** (1 / TUMBLE_EASE_POWER);

const distanceAt = (progress: number, length: number): number =>
  length * (1 - progress) ** TUMBLE_EASE_POWER;

const turnedAt = (leg: Leg, distance: number, radius: number): Mat3 =>
  multiply(axisAngle(leg.axis, (-(distance - leg.from) * TUMBLE_ROLL_RATE) / radius), leg.rest);

export const planTumble = ({
  area,
  start,
  landing,
  radius,
  face,
  rng,
}: TumbleRequest): TumblePlan => {
  const width = Math.max(area.right - area.left, 0);
  const height = Math.max(area.bottom - area.top, 0);
  const from = { x: clamp(start.x - area.left, 0, width), y: clamp(start.y - area.top, 0, height) };
  const to = {
    x: clamp(landing.x - area.left, 0, width),
    y: clamp(landing.y - area.top, 0, height),
  };

  const cells = pickCells(rng);
  const landingImage = { x: imageOf(to.x, width, cells.x), y: imageOf(to.y, height, cells.y) };
  const length = Math.hypot(from.x - landingImage.x, from.y - landingImage.y);
  const towardStart =
    length === 0
      ? { x: 0, y: 0 }
      : { x: (from.x - landingImage.x) / length, y: (from.y - landingImage.y) / length };

  const walls = [
    ...wallsAlong(landingImage.x, towardStart.x, length, width),
    ...wallsAlong(landingImage.y, towardStart.y, length, height),
  ].sort((a, b) => a - b);
  const ends = [0, ...walls, length];

  const legs: Leg[] = [];
  let rest = landingOrientation(face);
  for (let index = 0; index < ends.length - 1; index += 1) {
    const legFrom = ends[index] ?? 0;
    const legTo = ends[index + 1] ?? length;
    const middle = (legFrom + legTo) / 2;
    const flipX =
      width > 0 && isMirrored(Math.floor((landingImage.x + towardStart.x * middle) / width));
    const flipY =
      height > 0 && isMirrored(Math.floor((landingImage.y + towardStart.y * middle) / height));
    // The die travels from the start to the landing, against `towardStart`.
    const travelX = -towardStart.x * (flipX ? -1 : 1);
    const travelY = -towardStart.y * (flipY ? -1 : 1);
    const twist = (rng.next() * 2 - 1) * TUMBLE_TWIST;
    // The axis of a ball that rolls along the travel. The screen has y down, the die has y up.
    const axis = normalize([travelY, travelX, twist]);
    const leg: Leg = { from: legFrom, axis, rest };
    legs.push(leg);
    rest = turnedAt(leg, legTo, radius);
  }

  return {
    area,
    landingImage,
    towardStart,
    length,
    radius,
    legs,
    bounces: walls.map((wall) => progressAt(wall, length)).reverse(),
  };
};

/** The squash of the last wall, from 1 down and back to 1. */
const bonkScale = (plan: TumblePlan, progress: number): number => {
  const last = lastWhere(plan.bounces, (bounce) => bounce <= progress);
  if (last === undefined) return 1;
  const since = (progress - last) / TUMBLE_BONK_PORTION;
  return since >= 1 ? 1 : 1 - TUMBLE_BONK_SQUASH * Math.sin(Math.PI * since);
};

/** The die at `progress`, from 0 at the start of the roll to 1 at rest. */
export const poseAt = (plan: TumblePlan, progress: number): TumblePose => {
  const { area, landingImage, towardStart, length, radius } = plan;
  const moment = clamp(progress, 0, 1);
  const distance = distanceAt(moment, length);
  const leg = lastWhere(plan.legs, (candidate) => candidate.from <= distance) ?? plan.legs[0];
  if (!leg) throw new Error('a tumble plan has no legs');

  const grown =
    TUMBLE_START_SCALE + (1 - TUMBLE_START_SCALE) * Math.min(1, moment / TUMBLE_GROW_PORTION);
  return {
    x: area.left + fold(landingImage.x + towardStart.x * distance, area.right - area.left),
    y: area.top + fold(landingImage.y + towardStart.y * distance, area.bottom - area.top),
    scale: grown * bonkScale(plan, moment),
    orientation: turnedAt(leg, distance, radius),
  };
};
